import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'

export const SITE_URL = 'https://mindweb.site'
export const BRAND = 'MindWeb'
export const LOGO_URL = `${SITE_URL}/brand/logo.svg`

interface SeoProps {
  readonly title: string
  readonly description: string
  /** Route path used for canonical and og:url, e.g. "/pricing". */
  readonly path: string
  readonly noIndex?: boolean
}

/**
 * Per-route SEO head tags. Overrides the static defaults in index.html on the
 * client; the index.html defaults remain the no-JS fallback.
 */
export function Seo({ title, description, path, noIndex = false }: SeoProps) {
  const { i18n } = useTranslation()
  const htmlLang = i18n.language === 'zh' ? 'zh-CN' : 'en'
  const fullTitle = `${title} — ${BRAND}`
  const url = `${SITE_URL}${path}`

  return (
    <Helmet htmlAttributes={{ lang: htmlLang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />

      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* Single bilingual URL today: every hreflang points to the same path.
          When per-language URLs ship (?lang= or subpaths), split these. */}
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="zh" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
    </Helmet>
  )
}
